import {
  INotificationFE,
  INotification,
  INotificationDialogFE,
  NotificationGroup
} from "@/types/notifications";
import {
  createServerEndpoint,
  getEndOfDayValue,
  getFormattedDate,
  sortCaseNotifications
} from "@/utils/functions";
import axios from "axios";

const state = () => ({
  notifications: [] as INotification[]
});

const getters = {
  // Getting the first 5 notifications for display in the initial dropdown
  formattedNotifications(state: any): INotificationFE[] {
    return state.notifications.slice(0, 5).map(transform2BaseNotificationItem);
  },

  // ALL notifications grouped and sorted for the dialog
  formattedDialogNotifications(state: any): NotificationGroup {
    const grouped = state.notifications.reduce(
      (memo: NotificationGroup, current: INotification) => {
        const grouper = getEndOfDayValue(new Date(current.created));
        const title = getFormattedDate(
          new Date(current.created),
          "date-slash-year"
        );

        if (grouper in memo) {
          memo[grouper].data.push(transform2DialogNotificationItem(current));
        } else {
          memo[grouper] = {
            title: title,
            data: [transform2DialogNotificationItem(current)]
          };
        }

        return memo;
      },
      {}
    );

    const sorted: NotificationGroup = Object.keys(grouped)
      .sort(sortNotificationByTimestamp)
      .reduce((memo: NotificationGroup, current) => {
        memo[+current] = grouped[+current];
        return memo;
      }, {});

    return sorted;
  }
};

const mutations = {
  SET_NOTIFICATIONS(state: any, notifications: INotification[]) {
    state.notifications = notifications;
  }
};

const actions = {
  async FETCH_DATA({ commit }: any, id: string) {
    const endpointRaw = createServerEndpoint("/api/notifications/{user_id}");
    const endpoint = endpointRaw.replace("{user_id}", id);

    try {
      const { data } = await axios.get<INotification[]>(endpoint);
      commit("SET_NOTIFICATIONS", data.sort(sortCaseNotifications));
    } catch (error) {
      console.error(error);
      commit("SET_NOTIFICATIONS", []); // Set an empty array in case of an error
    }
  }
};

// Sort in descending order; i.e., from the most-recent date
function sortNotificationByTimestamp(current: string, next: string): number {
  return +next - +current;
}

// Translate raw-data to the desired frontend format: INotificationFE
function transform2BaseNotificationItem(
  notification: INotification
): INotificationFE {
  return {
    caseid: notification.case_id,
    title: notification.readable_id,
    description: getFormattedDescription(notification),
    timestamp: getFormattedDate(new Date(notification.created), "days-ago"),
    is_comment: notification.action_type === "comment-added"
  };
}

function getFormattedDescription(notification: INotification): string {
  const isAssignmentAction = "assignment-added" === notification.action_type;
  const isDeletionAction = "assignment-deleted" === notification.action_type;

  let formattedDescription: string;

  formattedDescription = `${notification.action_done_by_name} ${notification.action}`;

  if (isAssignmentAction || isDeletionAction) {
    formattedDescription = `${formattedDescription} ${notification.impacted_by}`;
  }

  return formattedDescription;
}

// Translate raw-data to the desired frontend format
function transform2DialogNotificationItem(
  notification: INotification
): INotificationDialogFE {
  return {
    ...transform2BaseNotificationItem(notification),
    subtitle_timestamp: getFormattedDate(
      new Date(notification.created),
      "date-slash-time"
    )
  };
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
